import React from "react";
import { Route } from "react-router-dom";
import {
  match as matchPropTypes,
  location as locationPropTypes,
} from "react-router-prop-types";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { AhrMainNav } from "src/Layout/AppNav/NavItems";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";

// Pages
import TilesMenu from "src/Components/TilesMenu";
import SecureRoute from "src/utils/RoleBasedSecurity/SecurityComponents/SecureRoute";
import {
  employeeEmployeePermissionRead,
  notificationAppMessagePermissionRead,
  companyReportAccessPasswordRead,
  proposalsModulePermissionRead,
  totalRewardModule,
  totalRewardSettings,
  totalRewardPerkUsersRead,
  totalRewardPerkUsersImportExport,
  totalRewardPerksRead,
  totalRewardPerksWrite,
  totalRewardPerkCategoriesRead,
  totalRewardPerkCategoriesWrite,
  organizationStructureModuleRead,
  organizationStructureStructureCreate,
  absencesModuleRead,
  organizationStructureEmployeeDataExport,
  companySubscriptionsWaitingRoom,
  companyMultisportCardWaitingRoom,
  jobManagerProcessRead,
  organizationStructureEmployeeDataRead,
  organizationStructureAreaRead,
} from "src/utils/RoleBasedSecurity/permissions";
import Proposals from "src/External/proposals";
import { useCompanyName } from "src/Pages/Company/CompanyContext";
import Password from "src/Pages/Password/Edit";
import __ from "src/utils/Translations";
import Absences from "src/Pages/Company/Absences";
import OrganizationStructure from "src/Pages/Company/OrganizationStructure/routes";
import CreateStructure from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure";
import DataManagement from "src/Pages/Company/OrganizationStructure/DataManagment";
import ModuleSettings from "src/Pages/Company/OrganizationStructure/ModuleSettings";
import OrganizationStructureProviders from "src/Pages/Company/OrganizationStructure/Providers/OrganizationStructureProviders";
import ImportsHistory from "src/Pages/Company/ImportsHistory";
import ImportsView from "src/Pages/Company/ImportsHistory/View";
import CompanyEmployeesList from "./CompanyEmployees/List";
import CompanyEmployeesEdit from "./CompanyEmployees/Edit";
import PendingBenefits from "./BenefitsPending";
import CompanyEvents from "./CompanyEvents";
import TotalRewardSettings from "./TotalRewardStatement/Settings";
import TotalRewardPerkUsers from "./TotalRewardStatement/PerkUsers";
import TotalRewardImportExport from "./TotalRewardStatement/Import";
import TotalRewardPerks from "./TotalRewardStatement/Perks";
import TotalRewardPerkEdit from "./TotalRewardStatement/Perks/edit";
import TotalRewardCategories from "./TotalRewardStatement/Categories";
import TotalRewardCategoryEdit from "./TotalRewardStatement/Categories/edit";
import AccessToken from "./AccessToken";
import Report from "./Report";
import AppMessagesList from "./Notification/AppMessages/List";
import AppMessagesEdit from "./Notification/AppMessages/Edit";

const AhrCore = ({ match, location }) => {
  const companyName = useCompanyName();
  const ahrTilesNav = AhrMainNav.map((item) => {
    if (item.tail === false) {
      return null;
    }
    return item;
  }).filter(Boolean);
  const hasAbsencesEnabled = useFeatureIsOn(FEATURE_FLAGS.ABSENCES);
  const hasOrganizationStructureEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE
  );

  return (
    <>
      <Route
        exact
        path={`${match.url}`}
        render={() => (
          <TilesMenu
            config={ahrTilesNav}
            title={`${__("Zarządzanie firmą")} ${companyName}`}
            breadcrumbs={[]}
          />
        )}
      />
      <SecureRoute
        permission={employeeEmployeePermissionRead}
        exact
        path={`${match.url}/employees`}
        component={CompanyEmployeesList}
      />
      <SecureRoute
        permission={employeeEmployeePermissionRead}
        exact
        path={`${match.url}/employees/:employeeId`}
        component={CompanyEmployeesEdit}
      />
      <SecureRoute
        permission={[
          organizationStructureAreaRead,
          organizationStructureEmployeeDataRead,
        ]}
        exact
        path={`${match.url}/imports-history`}
        component={ImportsHistory}
      />
      <SecureRoute
        permission={[
          organizationStructureAreaRead,
          organizationStructureEmployeeDataRead,
        ]}
        path={`${match.url}/imports-history/show/:importId`}
        exact
        heading="Podgląd importu"
        component={ImportsView}
      />
      <SecureRoute
        permission={[
          companySubscriptionsWaitingRoom,
          companyMultisportCardWaitingRoom,
        ]}
        exact
        path={`${match.url}/pending-benefits`}
        component={PendingBenefits}
      />
      <SecureRoute
        permission={[
          companySubscriptionsWaitingRoom,
          companyMultisportCardWaitingRoom,
        ]}
        exact
        path={`${match.url}/pending-benefits-multisport`}
        component={PendingBenefits}
      />
      <SecureRoute
        permission={companyReportAccessPasswordRead}
        exact
        path={`${match.url}/access`}
        component={AccessToken}
      />
      <Route path={`${match.url}/company-events`} component={CompanyEvents} />
      <SecureRoute
        permission={proposalsModulePermissionRead}
        path={`${match.url}/proposals`}
        component={Proposals}
      />
      <SecureRoute
        permission={totalRewardModule}
        path={`${match.url}/total-reward`}
        exact
        render={() => (
          <TilesMenu
            config={
              AhrMainNav.find((el) => el.id === "totalRewardStatement").content
            }
            title="Total Reward Statement"
            breadcrumbs={[
              {
                title: `${__("Zarządzanie firmą")} ${companyName}`,
                link: `${match.url}`,
              },
            ]}
          />
        )}
      />
      <SecureRoute
        permission={totalRewardSettings}
        path={`${match.url}/total-reward/settings`}
        exact
        component={TotalRewardSettings}
      />
      <SecureRoute
        permission={totalRewardPerkUsersRead}
        path={`${match.url}/total-reward/perk-users`}
        exact
        component={TotalRewardPerkUsers}
      />
      <SecureRoute
        permission={totalRewardPerkUsersImportExport}
        path={`${match.url}/total-reward/perkusers-import`}
        exact
        component={TotalRewardImportExport}
      />
      <SecureRoute
        permission={totalRewardPerksRead}
        path={`${match.url}/total-reward/perks`}
        exact
        component={TotalRewardPerks}
      />
      <SecureRoute
        permission={totalRewardPerksWrite}
        path={`${match.url}/total-reward/perks/:perkId`}
        exact
        component={TotalRewardPerkEdit}
      />
      <SecureRoute
        permission={totalRewardPerkCategoriesRead}
        path={`${match.url}/total-reward/categories`}
        exact
        component={TotalRewardCategories}
      />
      <SecureRoute
        permission={totalRewardPerkCategoriesWrite}
        path={`${match.url}/total-reward/categories/:categoryId`}
        exact
        component={TotalRewardCategoryEdit}
      />
      <Route path={`${match.url}/report`} component={Report} />
      <Route
        exact
        path={`${match.url}/notification`}
        render={() => (
          <TilesMenu
            config={AhrMainNav.find((el) => el.id === "notification").content}
            title={__("Powiadomienia")}
            breadcrumbs={[
              {
                title: `${__("Zarządzanie firmą")} ${companyName}`,
                link: `${match.url}`,
              },
            ]}
          />
        )}
      />
      <SecureRoute
        permission={notificationAppMessagePermissionRead}
        exact
        path={`${match.url}/notification/app-messages`}
        component={AppMessagesList}
      />
      <SecureRoute
        permission={notificationAppMessagePermissionRead}
        exact
        path={`${match.url}/notification/app-messages/:appMessageId`}
        component={AppMessagesEdit}
      />
      {/* Password change */}
      <Route path="/ahr/password" component={Password} />

      {hasAbsencesEnabled ? (
        <SecureRoute
          path="/ahr/absences"
          component={Absences}
          permission={absencesModuleRead}
        />
      ) : null}

      {hasOrganizationStructureEnabled && (
        <>
          <SecureRoute
            permission={organizationStructureModuleRead}
            path={`${match.url}/organization-structure`}
            component={OrganizationStructure}
          />
          <SecureRoute
            permission={organizationStructureModuleRead}
            exact
            path={`${match.url}/organization-structure`}
            render={() => (
              <TilesMenu
                config={
                  AhrMainNav.find((el) => el.id === "organization_structure")
                    .content
                }
                id="organization_structure"
                title={__("Struktura Organizacji")}
                location={location}
                breadcrumbs={[
                  {
                    title: `${__("Zarządzanie firmą")} ${companyName}`,
                    link: `${match.url}`,
                  },
                ]}
              />
            )}
          />
        </>
      )}
    </>
  );
};

export default AhrCore;

AhrCore.propTypes = {
  match: matchPropTypes.isRequired,
  location: locationPropTypes.isRequired,
};
